// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import * as actionTypes from '../../call/interfaceNew/actionTypes'

import { getCallById, getCallByWebrtcSessionId, getCalls, getOptions } from '../../call/interfaceNew/selectors'
import { CALL_STATES, CALL_MEDIA_CONNECTION_STATES, ICE_COLLECTION_RESULT_TYPES } from '../../call/constants'

// Callstack plugin.
import { closeCall } from '../webrtc/midcall'

// Other plugins.
import { logManager } from '../../logs'
import { sessionActions } from '../../webrtc/interface/actions'
import * as webrtcActionTypes from '../../webrtc/interface/actionTypes'
import BasicError, { callCodes } from '../../errors'

// Libraries.
import { all, call, put, race, select, take, delay } from 'redux-saga/effects'

/**
 * Performs a call audit and manages the audit loop.
 *
 * This saga is responsible for sending a call audit repeatedly on a consistent interval.
 *  Consistency is ensured by calculating the time it takes to perform the actual
 *  audit request, and subtracting that from the configured call audit timer.
 *
 * The saga determines which interval to use for the delay between requests by analyzing
 *  the response to the audit request:
 *    - for a successful response we use __Max(10s, configured `callAuditTimer`) - request duration__
 *    - for an error response we use __Min(5s, 45% of `callAuditTimer`) - request duration__
 *    - for a "Closed" status response, we stop the audit loop and tear down the call
 *
 * Responsibilities:
 *    1. Determine if an audit should be sent.
 *        - Check call's state.
 *    2. Perform signaling (if needed).
 *    3. Return the status of the audit
 * @method sendCallAudit
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.auditCall "Call audit" signalling function.
 * @param {Object}   action        The action that triggered the audit.
 */
export function * sendCallAudit (deps, action) {
  const { webRTC, requests } = deps
  let currentCall = yield select(getCallById, action.payload.id)

  const log = logManager.getLogger('CALL', currentCall ? action.payload.id : '')

  // Basic validation
  if (!currentCall) {
    log.debug(`Call ${action.payload.id} not found; no audit loop needed.`)
    return
  }

  const config = yield select(getOptions)
  const successDelay = Math.max(10000, config.callAuditTimer)
  const errorDelay = Math.min(5000, config.callAuditTimer * 0.45)

  log.info('Starting audit loop for new call.')
  while (true) {
    const lastAudit = Date.now()

    currentCall = yield select(getCallById, action.payload.id)

    if (currentCall.state === CALL_STATES.ENDED) {
      log.info('Call has ended; stopping audit loop.')
      return
    }

    // Race between the audit and the audit request timeout of 4s
    log.info('Sending call audit.')
    const { audit, timeout } = yield race({
      audit: call(requests.auditCall, {
        wrtcsSessionId: currentCall.wrtcsSessionId,
        isAnonymous: currentCall.isAnonymous,
        account: currentCall.account
      }),
      timeout: delay(4000)
    })

    /*
     * Audit response error scenarios include the response having an `error: true` and `status` property.
     *  The status will be either 'Retry' or 'Closed' (depending on statusCode we get from response),
     *  and nothing else. See `auditCall` request sagas.
     * Success scenarios will have `error: false` along with the call's `status`.
     */
    if (audit && audit.status === 'Retry') {
      // We have an error but status is not Closed, so we continue sending audit request in hoping audit will recover
      const { message, code } = audit.error
      log.debug(`Call audit failed, caused by ${message} (${code}); will retry failed audit for call.`)
      const auditDuration = Date.now() - lastAudit
      yield delay(errorDelay - auditDuration)
    } else if (audit && audit.status === 'Closed') {
      // If this audit returned 'Closed', then the audit loop should stop right away.
      log.info(`Ending audit loop and Call due to Closed status. Changing to ${CALL_STATES.ENDED}.`)

      // Hangup call automatically (from webRTC perspective)
      yield call(closeCall, webRTC, currentCall.webrtcSessionId)

      const latestCallState = yield select(getCallById, action.payload.id)
      if (latestCallState.state !== CALL_STATES.ENDED && latestCallState.state !== CALL_STATES.CANCELLED) {
        // Cleanup Redux state by sending END_CALL_FINISH action
        yield put(
          callActions.endCallFinish(currentCall.id, {
            isLocal: true,
            transition: { statusCode: 9909, reasonText: 'Call has ended due to call audit failure.' }
          })
        )
      }
      return
    } else if (audit) {
      log.debug(`Call audit status is ${audit.status}.`)
      const auditDuration = Date.now() - lastAudit
      yield delay(successDelay - auditDuration)
    } else if (timeout) {
      // No need to cancel the audit task since the effect that loses the race is automatically cancelled
      log.debug('Audit request timed out, retrying in 1 second.')
      yield delay(1000)
    } else {
      log.debug('Audit loop encountered an unknown scenario; ending.')
      return
    }
  }
}

/**
 * Filters which call needs to be re-synched and calls the helper saga (`updateCallState`) to perform the re-synching.
 *
 * Responsibilities:
 *    1. Ensure resyncOnConnect config is set
 *    2. Filter out inactive calls (calls already in an ended state)
 *    3. In parallel, use the helper saga (`updateCallState`) to perform the re-synching of the active calls
 * @method sendCallAudit
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   action        The action that triggered the audit.
 */
export function * getSessions (deps, action) {
  const config = yield select(getOptions)
  if (!config.resyncOnConnect) {
    return
  }
  // Grab all active calls
  const calls = yield select(getCalls)
  const activeCalls = calls.filter(call => call.state !== CALL_STATES.CANCELLED && call.state !== CALL_STATES.ENDED)

  // Check and update the call state of each call if necessary
  yield all(activeCalls.map(activeCall => call(updateCallState, deps, activeCall)))
}

// eslint-disable-next-line no-warning-comments
/**
 * Sends a GET session request and updates call if required.
 *
 * This saga performs the signaling operation to get the status of a call session on the server.
 *    There are no local webRTC operations involved.
 *
 * Assumptions:
 *    1. The server uses WebRTC Gateway 4.7.1+
 * Responsibilities:
 *    2. Update the call state if the call is out of sync (call's status does not match response from server)
 * @method updateCallState
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.getSession GET session signalling function.
 * @param {Object}   action        The call being acted on.
 */
export function * updateCallState (deps, activeCall) {
  const { webRTC, requests } = deps
  const log = logManager.getLogger('CALL', activeCall.id)
  const callStateAfterConnect = activeCall.state

  // If there is an ongoing operation when the WS connects, the response to that operation will re-sync the state
  // Otherwise, we need to get the status of the session from the server
  if (!activeCall.localOp && !activeCall.remoteOp) {
    const sessionStatusResponse = yield call(requests.getSession, {
      wrtcsSessionId: activeCall.wrtcsSessionId
    })

    // Get state of the call again before evaluating the response as an operation request and response
    // could have been processed in between the request to get the session and the response received
    const currentCall = yield select(getCallById, activeCall.id)

    // Do nothing if an operation was triggered in between the GET sessions request and response, and is ongoing
    if (!currentCall.localOp && !currentCall.remoteOp) {
      /*
       * If the current call state is ringing OR if the state of the call was connected/on hold before we
       * did the GET, look at the response of the GET request.
       * If call is ringing and GET session returns ringing, we don't need to do anything
       * Also, if we were previously connected, but GET returns ringing, this could be due to an operation that was resolved
       * between the GET request and response. In any case, no need to look at GET response for this scenario.
       */
      if (currentCall.state === CALL_STATES.RINGING || callStateAfterConnect !== CALL_STATES.RINGING) {
        // Call not found
        if (sessionStatusResponse.error && sessionStatusResponse.error.code === 47) {
          // End the call as the session does not exist on the server anymore (statusCode 47 response)
          yield call(closeCall, webRTC, activeCall.webrtcSessionId)

          log.info(`Call re-sync found that call is ended. Changing to ${CALL_STATES.ENDED}.`)
          yield put(
            callActions.endCallFinish(activeCall.id, {
              isLocal: true,
              error: sessionStatusResponse.error
            })
          )

          // GET response errors other than session not found
        } else if (sessionStatusResponse.error) {
          log.debug(`Call re-sync failure (${sessionStatusResponse.error.code}).`, sessionStatusResponse.error)

          // If the call is answered, but not by us
        } else if (
          sessionStatusResponse.state === 'ANSWERED' &&
          currentCall.state !== CALL_STATES.CONNECTED &&
          currentCall.state !== CALL_STATES.ON_HOLD
        ) {
          // Report call as cancelled
          yield call(closeCall, deps.webRTC, activeCall.webrtcSessionId)
          log.info(`Call re-sync found that call is cancelled. Changing to ${CALL_STATES.CANCELLED}.`)
          yield put(callActions.callCancelled(activeCall.id))
        }
      }
    }
  }
}

/**
 * Sends a GET session request and updates the call if necessary.
 * The response to the GET session request contains two properties that can be used for determining the call state:
 *  1. `statusCode` - Code which can be used to provide more info for error responses
 *  2. `state`
 *    a. "RINGING" - Call exists, and can be either accepted or rejected.
 *    b. "ANSWERED" - Call is active, and can be either Connected or Held.
 *
 * This saga performs the signaling operation to get the status of a call session on the server, and any local
 *    WebRTC operations necessary for closing the call if necessary.
 *
 * Responsibilities:
 *    1. Update the call state if the call is out of sync (call's status does not match the response from the server)
 * @method resyncCallState
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.getSession GET session signalling function.
 * @param {Object}   action        The call being acted on.
 */
export function * resyncCallState (deps, action) {
  const { webRTC, requests } = deps
  const log = logManager.getLogger('CALL', action.payload.id)

  const currentCall = yield select(getCallById, action.payload.id)
  if (!currentCall) {
    log.debug(`Call ${action.payload.id} not found.`)
    yield put(
      callActions.resyncFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call not found; invalid call ID.'
        })
      })
    )
    return
  }

  const sessionStatusResponse = yield call(requests.getSession, {
    wrtcsSessionId: currentCall.wrtcsSessionId
  })

  // Call not found
  if (sessionStatusResponse.error && sessionStatusResponse.error.code === 47) {
    log.info('Call re-sync found that the call has ended. Ending Call.')
    // End the call as the session does not exist on the server anymore (statusCode 47 response)
    yield call(closeCall, webRTC, currentCall.webrtcSessionId)

    yield put(callActions.resyncFinish(currentCall.id))
    const latestCallState = yield select(getCallById, action.payload.id)
    if (latestCallState.state !== CALL_STATES.ENDED && latestCallState.state !== CALL_STATES.CANCELLED) {
      yield put(
        callActions.endCallFinish(currentCall.id, {
          isLocal: true
        })
      )
    }
  } else if (sessionStatusResponse.error) {
    // GET response errors other than session not found
    log.info(`Call re-sync failure (${sessionStatusResponse.error.code}).`, sessionStatusResponse.error)
    yield put(
      callActions.resyncFinish(currentCall.id, {
        error: sessionStatusResponse.error
      })
    )
  } else if (
    sessionStatusResponse.state === 'ANSWERED' &&
    currentCall.state !== CALL_STATES.CONNECTED &&
    currentCall.state !== CALL_STATES.ON_HOLD
  ) {
    log.info('Call re-sync found that call is cancelled. Cancelling call.')
    // If the call is answered, but not by us, report call as cancelled
    yield call(closeCall, deps.webRTC, currentCall.webrtcSessionId)
    yield put(callActions.resyncFinish(currentCall.id))
    const latestCallState = yield select(getCallById, action.payload.id)
    if (latestCallState.state !== CALL_STATES.ENDED && latestCallState.state !== CALL_STATES.CANCELLED) {
      yield put(callActions.callCancelled(currentCall.id))
    }
  } else {
    log.info('Call re-sync found that there are no changes necessary for the call')
    yield put(callActions.resyncFinish(currentCall.id))
  }
}

/**
 * This saga is meant to normalize ICE connection state changes between Plan-B
 *    and Unified-Plan PeerConnections.
 *
 * Expectation: When ICE connection has an issue, it moves into `disconnected`
 *    state. If it can't automatically fix the issue, it should move into `failed`
 *    state shortly afterwards.
 * Reality: Unified-Plan behaves a little differently, where it never transitions
 *    into `failed` state. Plan-B uses heuristics to know when to give up and
 *    transition into `failed` state, but Unified-Plan does not.
 * Ref: https://docs.google.com/document/d/1-ZfikoUtoJa9k-GZG1daN0BU3IjIanQ_JSscHxQesvU/edit#heading=h.xy9xt6bk24e
 *
 * So this saga is meant to "fill-in" that missing behaviour for Unified-Plan:
 *    If a Unified-Plan call enters `disconnected` state, we want to wait a short
 *    amount of time (due to lacking any real heuristic). If the call is still
 *    disconnect after that time, we assume it should be in `failed` state and
 *    manually update the call state.
 *
 * The purpose for doing this is to enter other behaviours that rely on the `failed`
 *    state (eg. ICE restart, telling the application there is no media) still occur.
 *
 * @param  {Action} action A "ICE connection state change" action specifically
 *    for the disconnected state.
 */
export function * normalizeIceFailure (action) {
  /**
   * Determines if a call is in limbo disconnected media state.
   * @param  {CallObject}  targetCall
   * @return {boolean} Whether the call is on-going but in disconnected ICE state.
   */
  function isDisconnected (targetCall) {
    // 1) Call exists, 2) Call is on-going, 3) Call's media is disconnected.
    return (
      targetCall &&
      ![CALL_STATES.ENDED, CALL_STATES.CANCELLED].includes(targetCall.state) &&
      targetCall.mediaConnectionState === CALL_MEDIA_CONNECTION_STATES.DISCONNECTED
    )
  }

  let currentCall = yield select(getCallByWebrtcSessionId, action.payload.id)
  const options = yield select(getOptions)
  if (options.defaultPeerConfig.sdpSemantics !== 'unified-plan') {
    // We only want to act when the PeerConnection is configured for Unified-Plan.
    return
  } else if (!isDisconnected(currentCall)) {
    // We only want to act if the call is currently disconnected.
    return
  }

  /*
   * Wait a short time to let the Call try fixing things itself.
   * The number "10000" was determined by comparing how long it takes for the
   *    disconnected-->failed transition to happen when on Plan-B. It consistently
   *    took [almost] exactly 10 seconds for the change to happen, no matter
   *    if it was an audio-only or audio+video call.
   * Note: This test was done on Chrome, since it is the only browser to support
   *    Plan-B.
   * Note: 10s is also Chrome's timeout for collecting ICE candidates on an
   *    interface. I assume this isn't a coincidence.
   */
  const { ended, changedState } = yield race({
    timeout: delay(10000),
    // Watch for if this call ends while we're delaying.
    ended: take(action => {
      return action.type === actionTypes.END_CALL_FINISH && action.payload.id === currentCall.id
    }),
    // Watch for if this call's media connection state changes again.
    changedState: take(action => {
      return (
        action.type === webrtcActionTypes.SESSION_ICE_CONNECTION_STATE_CHANGE &&
        action.payload.id === currentCall.webrtcSessionId
      )
    })
  })

  if (ended || changedState) {
    // If the call has ended, or is no longer disconnected, do nothing.
    return
  }

  // Get the call state again, to make sure we have the latest state.
  currentCall = yield select(getCallByWebrtcSessionId, action.payload.id)
  // If the call is still disconnected, assume that it should have moved into
  //    failed state. Manually trigger the action to transition into failed state.
  if (isDisconnected(currentCall)) {
    const log = logManager.getLogger('CALL', currentCall.id)
    log.debug(
      'Call has remained in disconnected media connection state too long. Manually transitioning to failed state.'
    )

    yield put(
      sessionActions.sessionIceConnectionStateChange(currentCall.webrtcSessionId, {
        iceConnectionState: CALL_MEDIA_CONNECTION_STATES.FAILED
      })
    )
  }
}

/**
 * Add the necessary information to the ice collection info object and call the
 *  ice collection check function. Then pass the results of the check back to the
 *  WebRTC stack for further processing
 *
 * @method callIceCollectionCheck
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   action        The action being acted on.
 */
export function * callIceCollectionCheck (deps, action) {
  const { webRTC } = deps

  const { id: webrtcSessionId, ...iceCollectionInfo } = action.payload
  const currentCall = yield select(getCallByWebrtcSessionId, webrtcSessionId)
  const log = logManager.getLogger('CALL', currentCall ? action.payload.id : '')
  const config = yield select(getOptions)

  log.debug('Calling ICE collection check function for reason:', iceCollectionInfo.reason)
  let result
  try {
    result = config.iceCollectionCheckFunction(
      {
        callId: currentCall.id,
        callOperation: currentCall.localOp && currentCall.localOp.operation,
        ...iceCollectionInfo
      },
      {
        // TODO: If a call changes these configs in the middle of ICE collection,
        //    it will change the values given to this function. Very unlikely scenario,
        //    but could cause issues. Could also change `iceCollectionCheckFunction` itself.
        iceCollectionIdealTimeout: config.iceCollectionIdealTimeout,
        iceCollectionMaxTimeout: config.iceCollectionMaxTimeout
      }
    )
  } catch (e) {
    log.info('Error thrown by the iceCollectionCheckFunction. Ending call.', e)
    result = {
      type: 'Error',
      error: e
    }
  }

  // Get the webrtc Session for the call.
  const session = yield call([webRTC.sessionManager, 'get'], webrtcSessionId)
  // Get the remote description for the Session.
  log.debug('Handling ICE collection check result:', result.type)
  yield call([session, 'iceCollectionCheckResult'], result)

  // If the result is neither 'StartCall' or 'Wait', then it is either an error or an undefined result type.
  //   In either case, the session will cleanup the WebRTC portions of the call so just cleanup the state here.
  if (result.type !== ICE_COLLECTION_RESULT_TYPES.START_CALL && result.type !== ICE_COLLECTION_RESULT_TYPES.WAIT) {
    yield put(
      callActions.endCallFinish(currentCall.id, {
        isLocal: true,
        error: result.error || true
      })
    )
  }
}
